/**
 * The signed-in user's id, read from the session token itself. The Descope SDK's `claims` are not
 * always populated (depending on how the session was established), but the session JWT always carries
 * `sub`. Unverified by design: it only keys per-user client state — the server authorizes every
 * request from the token it verifies.
 */
export function sessionSubject(sessionToken) {
    const sub = sessionPayload(sessionToken)?.sub;
    return typeof sub === 'string' && sub.length > 0 ? sub : undefined;
}
/**
 * What the session token grants: its tenant roles and its admin claim (an entered tenant, a service
 * role). Two tokens of one user with the same access give the same string, so a token refresh that
 * changes nothing keeps per-user state, and one that changes access does not.
 */
export function sessionAccess(sessionToken) {
    const payload = sessionPayload(sessionToken);
    return JSON.stringify(canonical([payload?.tenants ?? null, payload?.admin ?? null]));
}
// Object keys and array items in a fixed order, so equivalent grants serialize identically.
function canonical(value) {
    if (Array.isArray(value)) {
        return value.map(canonical).sort((a, b) => {
            const left = JSON.stringify(a);
            const right = JSON.stringify(b);
            return left < right ? -1 : left > right ? 1 : 0;
        });
    }
    if (typeof value === 'object' && value !== null) {
        return Object.fromEntries(Object.keys(value).sort().map((key) => [key, canonical(value[key])]));
    }
    return value;
}
function sessionPayload(sessionToken) {
    const payload = sessionToken?.split('.')[1];
    if (!payload)
        return undefined;
    try {
        const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(payload.length / 4) * 4, '='));
        const parsed = JSON.parse(json);
        return typeof parsed === 'object' && parsed !== null ? parsed : undefined;
    }
    catch {
        return undefined;
    }
}
//# sourceMappingURL=sessionSubject.js.map